import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Tag
} from 'antd'
import dayjs from 'dayjs'

import {
  useClassifierServiceGetCommonClassifierElementListEndpoint,
  useTreatmentServiceGetTbScreeningContextEndpoint
} from 'src/api/queries'
import {
  ChestXrayResult,
  ClassifierCategory,
  ClassifierType,
  TbTestResult,
  XpertMtbRifResult
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { Text } from 'src/components'
import { useClassifierId } from 'src/hooks'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { isTbScreeningPositive } from './tbScreeningMappers'

const CRP_THRESHOLD = 5
const LF_LAM_CD4_OUTPATIENT = 100
const LF_LAM_CD4_INPATIENT = 200

const POSITIVE_XPERT = [
  XpertMtbRifResult.DETECTED_RIF_SENSITIVE,
  XpertMtbRifResult.DETECTED_RIF_RESISTANT,
  XpertMtbRifResult.DETECTED_RIF_INDETERMINATE
]

/** System keys of the investigation categories requested for active TB. */
const TB_CATEGORY_KEYS = {
  crp: 'tbCReactiveProtein',
  xpert: 'tbXpertMtbRif',
  lfLam: 'tbLfLam'
} as const

const SYMPTOMS = [
  'cough',
  'fever',
  'weightLoss',
  'nightSweats',
  'recentTbContact'
] as const

const SYMPTOM_LABELS: Record<(typeof SYMPTOMS)[number], string> = {
  cough: 'tb_screening.cough',
  fever: 'tb_screening.fever',
  weightLoss: 'tb_screening.weight_loss',
  nightSweats: 'tb_screening.night_sweats',
  recentTbContact: 'tb_screening.recent_tb_contact'
}

type Props = {
  form: FormInstance
  personId?: string
  /** Opens the TB investigation request with the recommended categories ticked. */
  onRequestLaboratory?: (categoryIds: string[]) => void
}

/** TB screening of every visit (protocol item 10): the four symptoms and a
 *  recent TB contact; a positive screening opens the examination for active
 *  TB (CRP, chest X-ray, LF-LAM by CD4, Xpert MTB/RIF, phthisiatrician). */
export const ConsultationTbScreeningCard = ({
  form,
  personId,
  onRequestLaboratory
}: Props) => {
  const { t } = useLocales()

  const screening = Form.useWatch('tbScreening', form)
  const isPositive = isTbScreeningPositive(screening)
  const workup = screening?.workup

  const { data: context } = useTreatmentServiceGetTbScreeningContextEndpoint(
    { personId: String(personId) },
    [personId],
    { enabled: !!personId && isPositive }
  )

  const cd4Threshold = screening?.isHospitalized
    ? LF_LAM_CD4_INPATIENT
    : LF_LAM_CD4_OUTPATIENT
  const latestCd4 = context?.latestCd4Count
  const cd4Date = context?.latestCd4Date
    ? dayjs(context.latestCd4Date).format(dayMonthYearFormatWithDot)
    : ''
  const isLfLamRecommended =
    latestCd4 !== undefined && latestCd4 !== null && latestCd4 < cd4Threshold

  // The recommended tests as investigation categories: CRP and Xpert MTB/RIF
  // always, LF-LAM when the latest CD4 is under the threshold.
  const investigationCategoryClassifierId = useClassifierId(
    ClassifierType.INVESTIGATION_CATEGORY,
    ClassifierCategory.INVESTIGATIONS
  )
  const { data: investigationCategories } =
    useClassifierServiceGetCommonClassifierElementListEndpoint(
      { id: String(investigationCategoryClassifierId), pageSize: 200 },
      [investigationCategoryClassifierId],
      { enabled: !!investigationCategoryClassifierId && isPositive }
    )

  const recommendedKeys: string[] = [
    TB_CATEGORY_KEYS.crp,
    TB_CATEGORY_KEYS.xpert,
    ...(isLfLamRecommended ? [TB_CATEGORY_KEYS.lfLam] : [])
  ]
  const recommendedCategoryIds = (investigationCategories?.data ?? [])
    .filter((item) => recommendedKeys.includes(String(item.originalValue)))
    .map((item) => String(item.id))

  const isTbConfirmed =
    workup?.lfLam === TbTestResult.POSITIVE ||
    POSITIVE_XPERT.includes(workup?.xpertMtbRif as XpertMtbRifResult)

  const yesNoOptions = [
    { value: true, label: t('common.yes') },
    { value: false, label: t('common.no') }
  ]

  const datePicker = (
    <DatePicker
      className="full-width"
      format={dayMonthYearFormatWithDot}
      disabledDate={disabledFutureDate}
    />
  )

  return (
    <Flex vertical gap={16}>
      <Flex vertical gap={4}>
        <Text size="xl" weight={700}>
          {t('tb_screening.title')}
        </Text>
        <Text weight={400}>{t('tb_screening.subtitle')}</Text>
      </Flex>

      <Row gutter={[24, 24]}>
        {SYMPTOMS.map((symptom) => (
          <Col key={symptom} xs={24} md={12} lg={8}>
            <Form.Item
              name={['tbScreening', symptom]}
              label={t(SYMPTOM_LABELS[symptom])}
              rules={requiredRules}
            >
              <Radio.Group options={yesNoOptions} />
            </Form.Item>
          </Col>
        ))}
      </Row>

      {isPositive && (
        <>
          <Alert
            type="warning"
            showIcon
            message={t('tb_screening.positive_alert')}
            description={
              <Flex vertical gap={4}>
                <Text weight={600}>{t('tb_screening.recommended')}</Text>
                <Text weight={400}>• {t('tb_screening.rec_crp')}</Text>
                <Text weight={400}>• {t('tb_screening.rec_xray')}</Text>
                <Text weight={400}>
                  • {t('tb_screening.rec_lf_lam', { threshold: cd4Threshold })}
                  {' — '}
                  {latestCd4 === undefined || latestCd4 === null ? (
                    t('tb_screening.cd4_unknown')
                  ) : isLfLamRecommended ? (
                    <Tag color="orange">
                      {t('tb_screening.lf_lam_recommended', {
                        value: latestCd4,
                        date: cd4Date
                      })}
                    </Tag>
                  ) : (
                    t('tb_screening.lf_lam_not_required', {
                      value: latestCd4,
                      date: cd4Date,
                      threshold: cd4Threshold
                    })
                  )}
                </Text>
                <Text weight={400}>• {t('tb_screening.rec_xpert')}</Text>
                <Text weight={400}>
                  • {t('tb_screening.rec_phthisiatrician')}
                </Text>
                {onRequestLaboratory && (
                  <Button
                    style={{ alignSelf: 'flex-start', marginTop: 8 }}
                    onClick={() => onRequestLaboratory(recommendedCategoryIds)}
                  >
                    {t('tb_screening.request_lab')}
                  </Button>
                )}
              </Flex>
            }
          />

          <Form.Item
            name={['tbScreening', 'isHospitalized']}
            valuePropName="checked"
          >
            <Checkbox>{t('tb_screening.is_hospitalized')}</Checkbox>
          </Form.Item>

          <Divider style={{ margin: 0 }} />

          <Text size="lg" weight={700}>
            {t('tb_screening.workup_title')}
          </Text>

          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Form.Item
                name={['tbScreening', 'workup', 'crpMgPerL']}
                label={t('tb_screening.crp')}
                extra={
                  (workup?.crpMgPerL ?? 0) > CRP_THRESHOLD && (
                    <Tag color="red">{t('tb_screening.crp_above')}</Tag>
                  )
                }
              >
                <InputNumber
                  className="full-width"
                  min={0}
                  max={1000}
                  step={0.1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['tbScreening', 'workup', 'crpDate']}
                label={t('tb_screening.crp_date')}
              >
                {datePicker}
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name={['tbScreening', 'workup', 'chestXray']}
                label={t('tb_screening.chest_xray')}
              >
                <Select
                  allowClear
                  placeholder={t('tb_screening.not_done')}
                  options={Object.values(ChestXrayResult).map((value) => ({
                    value,
                    label: t(`tb_screening.chest_xray_result.${value}`)
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['tbScreening', 'workup', 'chestXrayDate']}
                label={t('tb_screening.chest_xray_date')}
              >
                {datePicker}
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name={['tbScreening', 'workup', 'lfLam']}
                label={t('tb_screening.lf_lam')}
              >
                <Select
                  allowClear
                  placeholder={t('tb_screening.not_done')}
                  options={Object.values(TbTestResult).map((value) => ({
                    value,
                    label: t(`tb_screening.test_result.${value}`)
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['tbScreening', 'workup', 'lfLamDate']}
                label={t('tb_screening.lf_lam_date')}
              >
                {datePicker}
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name={['tbScreening', 'workup', 'xpertMtbRif']}
                label={t('tb_screening.xpert')}
              >
                <Select
                  allowClear
                  placeholder={t('tb_screening.not_done')}
                  options={Object.values(XpertMtbRifResult).map((value) => ({
                    value,
                    label: t(`tb_screening.xpert_result.${value}`)
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['tbScreening', 'workup', 'xpertMtbRifDate']}
                label={t('tb_screening.xpert_date')}
              >
                {datePicker}
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name={['tbScreening', 'workup', 'phthisiatricianReferral']}
                valuePropName="checked"
              >
                <Checkbox>{t('tb_screening.referral')}</Checkbox>
              </Form.Item>
            </Col>
            {workup?.phthisiatricianReferral && (
              <>
                <Col xs={24} md={12}>
                  <Form.Item
                    name={[
                      'tbScreening',
                      'workup',
                      'phthisiatricianReferralDate'
                    ]}
                    label={t('tb_screening.referral_date')}
                    rules={requiredRules}
                  >
                    {datePicker}
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name={[
                      'tbScreening',
                      'workup',
                      'phthisiatricianReferralNotes'
                    ]}
                    label={t('tb_screening.referral_notes')}
                    rules={[{ max: 1000 }]}
                  >
                    <Input.TextArea rows={2} maxLength={1000} showCount />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          {isTbConfirmed && (
            <Alert
              type="error"
              showIcon
              message={t('tb_screening.tb_positive_alert')}
            />
          )}
        </>
      )}
    </Flex>
  )
}
